import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

// A deploy replaces the hashed asset files, so a session that loaded the previous
// index.html 404s on lazy-loaded chunks. Vite reports exactly that as
// "vite:preloadError" — reload once to pick up the new index.html. The
// sessionStorage stamp stops a reload loop if the asset is missing for another
// reason (e.g. the request raced the deploy's restart window).
window.addEventListener("vite:preloadError", (event) => {
  const RELOAD_STAMP = "rme:chunk-reload-at";
  const last = Number(sessionStorage.getItem(RELOAD_STAMP) || 0);
  if (Date.now() - last < 30_000) return; // already tried — surface the error
  sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});
import axios from "axios";
import VueGtag from "vue-gtag";
import { captureException } from "./helpers/analytics";
import { installApiErrorReporting } from "./helpers/apiErrorReporting";
import { installSessionExpiryRedirect } from "./helpers/sessionExpiry";
import { apiInstance as organizationApi } from "./stores/organizationStore";
import { axiosInstance as subscriptionApi } from "./stores/subscriptionStore";

import "./style.css";
import App from "./App.vue";
import router from "./router";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  FontAwesomeIcon,
  FontAwesomeLayers,
} from "@fortawesome/vue-fontawesome";
import {
  faMagnifyingGlass,
  faMobileScreen,
  faDesktop,
  faPencil,
  faAngleDown,
  faAngleUp,
  faCircleInfo,
  faSquareCheck,
  faPaperclip,
  faXmark,
  faTrashCan,
  faPlusSquare,
  faBoxArchive,
  faPlus,
  faCopy,
  faDownload,
  faCloudArrowUp,
  faLock,
  faLockOpen,
  faLink,
  faList,
  faEnvelope,
  faInfoCircle,
  faMessage,
  faTags,
  faFile,
  faFileWord,
  faFilePdf,
  faFileZipper,
  faFilePowerpoint,
  faFileWaveform,
  faFileVideo,
  faFileImage,
  faFileCsv,
  faFileExcel,
  faFileText,
  faCirclePlus,
  faEye,
  faTriangleExclamation,
  faBell,
  faUnlock,
  faArrowUpWideShort,
  faArrowDownShortWide,
  faUserSecret,
  faShield,
  faLinkSlash,
  faFloppyDisk,
  faUpRightFromSquare,
  faArrowLeft,
  faCalendarXmark,
  faCheck,
  faFilterCircleXmark,
  faComment,
  faHashtag,
  faPlay,
  faPause,
  faRotateLeft,
  faCamera,
  faLocationPin,
  faBold,
  faUnderline,
  faItalic,
  faListOl,
  faListUl,
  faUndo,
  faRedo,
  faQuoteRight,
  faStrikethrough,
  faSpinner,
  faFlask,
  faCircleArrowUp,
  faWandMagicSparkles,
  faCommentSlash,
  faForwardStep,
  faBackwardStep,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faChevronRight,
  faChevronLeft,
  faUserPlus,
  faChevronUp,
  faChevronDown,
  faPalette,
  faUsers,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faYoutube,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import {
  faUser,
  faBellSlash,
  faClock,
  faCircleCheck,
} from "@fortawesome/free-regular-svg-icons";

/* add icons to the library */
library.add(
  faMagnifyingGlass,
  faMobileScreen,
  faDesktop,
  faFacebook,
  faTwitter,
  faInstagram,
  faYoutube,
  faUser,
  faPencil,
  faAngleDown,
  faAngleUp,
  faCircleInfo,
  faSquareCheck,
  faPaperclip,
  faXmark,
  faTrashCan,
  faPlusSquare,
  faBoxArchive,
  faPlus,
  faCopy,
  faDownload,
  faCloudArrowUp,
  faLock,
  faLockOpen,
  faLink,
  faList,
  faEnvelope,
  faInfoCircle,
  faMessage,
  faTags,
  faFile,
  faFileWord,
  faFilePdf,
  faFileZipper,
  faFilePowerpoint,
  faFileWaveform,
  faFileVideo,
  faFileImage,
  faFileCsv,
  faFileExcel,
  faFileText,
  faFile,
  faCirclePlus,
  faEye,
  faTriangleExclamation,
  faBell,
  faBellSlash,
  faUnlock,
  faLockOpen,
  faArrowUpWideShort,
  faArrowDownShortWide,
  faUserSecret,
  faShield,
  faLinkSlash,
  faFloppyDisk,
  faUpRightFromSquare,
  faArrowLeft,
  faLinkedin,
  faCalendarXmark,
  faCheck,
  faCircleCheck,
  faFilterCircleXmark,
  faComment,
  faHashtag,
  faPlay,
  faPause,
  faRotateLeft,
  faCamera,
  faLocationPin,
  faBold,
  faUnderline,
  faItalic,
  faListUl,
  faListOl,
  faUndo,
  faRedo,
  faQuoteRight,
  faStrikethrough,
  faSpinner,
  faFlask,
  faCircleArrowUp,
  faWandMagicSparkles,
  faCommentSlash,
  faClock,
  faCopy,
  faForwardStep,
  faBackwardStep,
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faChevronRight,
  faChevronLeft,
  faUserPlus,
  faChevronUp,
  faChevronDown,
  faPalette,
  faUsers,
  faKey
);

// Every 5xx the API returns now opens a PostHog issue naming the endpoint,
// whether or not the calling component lets the error surface.
installApiErrorReporting(axios);

// A 401 on a page that requires a login means the session has expired or been
// revoked: clear it and send the user to the login form with a return path.
// The two stores that keep their own axios instance need the same net.
for (const instance of [axios, organizationApi, subscriptionApi]) {
  installSessionExpiryRedirect(instance, router);
}

const app = createApp(App);

// Vue swallows errors thrown inside components — they never reach
// window.onerror, so PostHog's capture_exceptions cannot see them. Forward them
// by hand, and keep logging to the console so local debugging is unchanged.
app.config.errorHandler = (error, instance, info) => {
  console.error(error);
  captureException(error, {
    source: "vue",
    lifecycle_hook: info,
    component: instance?.$options?.__name || instance?.$options?.name,
  });
};

app.component("font-awesome-icon", FontAwesomeIcon);
app.component("font-awesome-layers", FontAwesomeLayers);
app.use(pinia);
app.use(router);

//app.use(PasswordMeter);
if (!window.__rmeSensitiveRoute) {
  app.use(
    VueGtag,
    {
      includes: [{ id: "G-2X09DQ1SJ1" }],
      config: { id: "UA-37047390-1" },
    },
    router
  );
}
app.mount("#app");
